import {
	AMM_RESERVE_PRECISION_EXP,
	BASE_PRECISION_EXP,
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
	ZERO,
} from '@drift-labs/sdk';
import { useEffect } from 'react';
import useAccountData from 'src/hooks/useAccountData';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import useBestBidAsk from 'src/hooks/useBestBidAsk';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useL2StateForMarket from '../../hooks/useL2StateForMarket';
import useMarketStateStore from '../../stores/useMarketStateStore';
import { useCurrentMarketStepSize } from 'src/hooks/useMarketStepSize';

const useTradeFormInputStateEngine = () => {
	const currentAccount = useAccountData();
	const set = useDriftStore((s) => s.set);

	const bestBidAsk = useBestBidAsk();

	const connected = useWalletIsConnected();

	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const selectedMarketId = useDriftStore((s) => s.selectedMarket.marketId);

	const l2State = useL2StateForMarket(
		selectedMarketId?.marketIndex,
		selectedMarketId?.marketType
	);

	const dlobIsLoaded =
		!!l2State && !!(l2State?.asks?.length || l2State?.bids?.length);

	const oraclePrice = useMarketStateStore((s) =>
		s.getOraclePriceForMarket(selectedMarketId)
	);

	const setBaseFormValue = (newBaseSize: string) => {
		set((s) => {
			s.tradeForm.baseSizeStringValue = newBaseSize;
		});
	};

	const setQuoteFormValue = (newQuoteSize: string) =>
		set((s) => {
			s.tradeForm.quoteSizeStringValue = newQuoteSize;
		});

	const clearBracketOrders = () =>
		set((s) => {
			s.tradeForm.bracketOrders = undefined;
		});

	const {
		tradeSide,
		leadSide,
		quoteSizeStringValue,
		baseSizeStringValue,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		orderType,
		estEntryPrice,
	} = useDriftStore((s) => ({
		tradeSide: s.tradeForm.side,
		quoteSizeStringValue: s.tradeForm.quoteSizeStringValue,
		baseSizeStringValue: s.tradeForm.baseSizeStringValue,
		priceBoxStringValue: s.tradeForm.priceBoxStringValue,
		secondaryPriceBoxStringValue: s.tradeForm.secondaryPriceBoxStringValue,
		leadSide: s.tradeForm.leadSide,
		orderType: s.tradeForm.orderType,
		selectedMarket: s?.selectedMarket?.current,
		estEntryPrice: s.tradeForm.priceImpact.entryPrice,
	}));

	const basePrecisionToUse = selectedMarket.isPerp
		? BASE_PRECISION_EXP
		: (selectedMarket.market as SpotMarketConfig).precisionExp;

	const rawBaseValue = BigNum.fromPrint(
		baseSizeStringValue,
		basePrecisionToUse
	);

	const stepSize = useCurrentMarketStepSize();

	// Listen for entry price / trade form updates to maintain correct state
	useEffect(() => {
		if (!bestBidAsk) return;

		set((s) => {
			s.tradeForm.stepSize = stepSize;
		});

		if (quoteSizeStringValue === '' && baseSizeStringValue === '') return;

		let priceToUse;
		const bestBidAskPrice =
			tradeSide === 'buy' ? bestBidAsk.bestAsk : bestBidAsk.bestBid;

		if (orderType == 'market') {
			// use markPrice as a backup for faster loading if priceImpact not calculated yet
			priceToUse =
				estEntryPrice && !estEntryPrice?.eq(ZERO)
					? BigNum.from(estEntryPrice, PRICE_PRECISION_EXP)
					: // Fallback to using oracle price if no dlob price is loaded
					!dlobIsLoaded && oraclePrice
					? oraclePrice
					: bestBidAskPrice
					? bestBidAskPrice
					: BigNum.zero(PRICE_PRECISION_EXP);
		} else {
			if (orderType == 'stopLimit' || orderType == 'takeProfitLimit') {
				priceToUse = secondaryPriceBoxStringValue
					? BigNum.fromPrint(secondaryPriceBoxStringValue, PRICE_PRECISION_EXP)
					: BigNum.zero(PRICE_PRECISION_EXP);
			} else {
				priceToUse = priceBoxStringValue
					? BigNum.fromPrint(priceBoxStringValue, PRICE_PRECISION_EXP)
					: BigNum.zero(PRICE_PRECISION_EXP);
			}
		}

		if (leadSide === 'quote') {
			if (priceToUse.eqZero()) {
				setBaseFormValue('');
				return;
			}

			const newBaseSize = BigNum.fromPrint(
				quoteSizeStringValue,
				QUOTE_PRECISION_EXP
			)
				.shift(AMM_RESERVE_PRECISION_EXP)
				.div(priceToUse.shiftTo(QUOTE_PRECISION_EXP));

			// if order type is oracle limit, price box is an offset so this calc doesnt make sense
			if (orderType !== 'oracleLimit') {
				setBaseFormValue(newBaseSize.toTradePrecision());
			}
		} else {
			if (priceToUse.eqZero()) {
				setQuoteFormValue('');
				return;
			}

			const newQuoteSize = priceToUse.mul(rawBaseValue);

			// avoid infinite loops
			if (newQuoteSize.toTradePrecision() === quoteSizeStringValue) {
				return;
			}

			setQuoteFormValue(newQuoteSize.toTradePrecision());
		}
	}, [
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		leadSide,
		baseSizeStringValue,
		quoteSizeStringValue,
		orderType,
		bestBidAsk,
		stepSize,
	]);

	const resetFormItems = () => {
		set((s) => {
			s.tradeForm.quoteSizeStringValue = '';
			s.tradeForm.baseSizeStringValue = '';
			s.tradeForm.maxLeverageSelected = false;
			s.tradeForm.showBracketOrderForm = false;
			s.tradeForm.bracketOrders = undefined;
		});
	};

	// reset form fields on market change
	useEffect(() => {
		resetFormItems();
	}, [selectedMarket?.market?.symbol, currentAccount?.userId, connected]);

	useEffect(() => {
		set((s) => {
			s.tradeForm.bracketOrders = undefined;
		});
	}, [orderType]);

	const appEventEmitter = useAppEventEmitter();

	// Reset the form when a transaction goes through successfully
	useEffect(() => {
		const tradeConfirmationHandler = () => {
			setQuoteFormValue('');
			setBaseFormValue('');
			clearBracketOrders();
		};

		appEventEmitter.on('tradeConfirmation', tradeConfirmationHandler);

		return () => {
			appEventEmitter.removeListener(
				'tradeConfirmation',
				tradeConfirmationHandler
			);
		};
	}, []);
};

export default useTradeFormInputStateEngine;
